import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '@app/core/auth/services';
import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { AlertService, UtilsService } from '@app/core/services';
import { DateTypes, SendByType } from '@app/shared/types';
import { InlineAlertTypes } from '@app/shared/types/inline-alert-types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, finalize, take } from 'rxjs';
import { RequestMessageAdd } from '../../models';
import { RequestMessageList } from '../../models/request-message-list';
import { RequestMessageService } from '../../services';
import { CommonModule } from '@angular/common';
import { TcDateStandaloneDirective } from '@app/shared/directives/tc-date-standalone.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StarRatingComponent } from '@app/shared/components/star-rating/star-rating.component';
import { MatIconModule } from '@angular/material/icon';
import { IRequestMessageWithdrawCryptoAddModel } from '../../models/request-message-withdraw-crypto-add.model';
import { AlertNewSnakbarService } from '@app/core/services/alert-new-snakbar.service';

@Component({
    selector: 'tc-request-message',
    imports: [
        TranslateModule,
        CommonModule,
        TcDateStandaloneDirective,
        MatProgressSpinnerModule,
        StarRatingComponent,
        ReactiveFormsModule,
        MatIconModule
    ],
    templateUrl: './request-message.component.html',
    styleUrls: ['./request-message.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RequestMessageComponent implements OnInit, OnDestroy {
  @Output() isSidebarLockedEmit = new EventEmitter<boolean>();
  @Input() requestId: string;
  @Input() canActive: boolean = true;
  @Input() isCryptoWithdraw: boolean = false;

  submitSubscription: Subscription;
  uploadImageSubscription: Subscription;
  downloadSubscription: Subscription;

  form: FormGroup;
  // data
  messages: RequestMessageList[];
  fetchMessageInterval: any;

  filesToUpload: File[];
  allowedExt: string;
  queueProgress: number | null;

  // types
  sendByType = SendByType;
  dateTypes = DateTypes;
  inlineAlertTypes = InlineAlertTypes;

  // boolean
  isLoading: boolean;
  isInvalid: boolean;

  showMessageeTextarea = false;
  isLoadingUploadImage: boolean = false;
  isLoadingSendMessage: boolean = false;
  isFirstLoad: boolean = true;

  textareaAnimationState = 'out';
  placeHolder = 'REQUEST.MESSAGE_PLACE_HOLDER';
  constructor(
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    private requestMessageService: RequestMessageService,
    private utilsService: UtilsService,
    private translateService: TranslateService,
    private authService: AuthService,
    // private alertService: AlertService,
    private alertNewSnakbarService: AlertNewSnakbarService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) { }

  /******************************************************
    LIFECYCLE HOOK
  *******************************************************/
  ngOnInit() {
    if (this.requestId) {
      this.messages = [];
      this.getMessages();
      this.fetchMessageInterval = setInterval(() => {
        this.getMessages();
      }, 60000);
    }
    this.initForm();
  }
  ngOnDestroy(): void {
    clearInterval(this.fetchMessageInterval);
    if (this.uploadImageSubscription) {
      this.uploadImageSubscription.unsubscribe();
    }
  }

  /******************************************************
    FORM
  *******************************************************/

  private initForm(): void {
    this.form = this.fb.group({
      comment: ['', Validators.compose([Validators.required, Validators.maxLength(500)])],
      attachment: [null],
      fileName: [null]
    });
    this.filesToUpload = [];
    this.allowedExt = this.utilsService.getAllowExtentions(this.appConfig.allowFileExtentions);
  }

  get f(): any {
    return this.form.controls;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.placeHolder = 'REQUEST.MESSAGE_PLACE_HOLDER';
      this.isInvalid = true;
      return;
    }

    if (this.isCryptoWithdraw) {
      const formModel: IRequestMessageWithdrawCryptoAddModel = Object.assign({});
      formModel.requestId = this.requestId;
      formModel.messageContent = this.f.comment.value;
      formModel.fileName = this.f.fileName.value;
      formModel.fileSize = this.filesToUpload.length > 0 ? this.filesToUpload[0].size : null;
      this.temporaryAddToMessage();
      this.closeSendMessage();
      this.isLoadingSendMessage = true;
      this.submitSubscription = this.requestMessageService.addNewMessageCryptoWithdraw(formModel)
        .pipe(
          finalize(() => this.isLoadingSendMessage = false)
        )
        .subscribe(
          (event: HttpEvent<any>) => {
            switch (event.type) {
              case HttpEventType.DownloadProgress:
              case HttpEventType.UploadProgress:
                if (event.total) {
                  this.queueProgress = Math.round(event.loaded / event.total * 100);
                }
                break;
              case HttpEventType.Response:
                this.handleResponse(event);
                break;
            }
          }, (error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.authService.logout();
            } else {
              this.queueProgress = null;
            }
          });
    } else {
      delete this.form.value['fileName'];
      const formModel: RequestMessageAdd = Object.assign({}, this.form.value);
      formModel.requestId = this.requestId;
      formModel.attachment = this.filesToUpload[0];
      this.temporaryAddToMessage();
      this.closeSendMessage();
      this.submitSubscription = this.requestMessageService.addNewMessage(formModel)
        .subscribe(
          (event: HttpEvent<any>) => {
            switch (event.type) {
              case HttpEventType.DownloadProgress:
              case HttpEventType.UploadProgress:
                if (event.total) {
                  this.queueProgress = Math.round(event.loaded / event.total * 100);
                }
                break;
              case HttpEventType.Response:
                this.handleResponse(event);
                break;
            }
          }, (error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.authService.logout();
            } else {
              this.queueProgress = null;
            }
          });
    }
  }

  /******************************************************
    Fetch
  *******************************************************/
  private getMessages(): void {
    if (this.isFirstLoad === true) {
      this.isLoading = true;
    } else {
      this.isLoading = false;
    }
    if (this.isCryptoWithdraw) {
      this.requestMessageService.getMessagesCryptoWithdraw(this.requestId)
        .pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
            this.isFirstLoad = false;
          })
        )
        .subscribe(
          (messages) => {
            if (this.messages.length !== messages.length) {
              this.messages = messages;
              this.cdr.detectChanges();
            }
          }
        );
    } else {
      this.requestMessageService.getMessages(this.requestId)
        .pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
            this.isFirstLoad = false;
          })
        )
        .subscribe(
          (messages) => {
            if (this.messages.length !== messages.length) {
              this.messages = messages.reverse();
              this.cdr.detectChanges();
            }
          }
        );
    }
  }

  /******************************************************
    Private
  *******************************************************/
  private handleResponse(event): void {
    this.queueProgress = null;
    this.getMessages();
  }

  /******************************************************
  Public
  *******************************************************/
  openSendMessage(): void {
    this.isSidebarLockedEmit.emit(true);
    this.textareaAnimationState = 'in';
    this.placeHolder = '';
    setTimeout(() => {
      this.isSidebarLockedEmit.emit(false);
    }, 800);
  }

  closeSendMessage(): void {
    if (this.filesToUpload && this.filesToUpload.length > 0) {
      return;
    }
    this.textareaAnimationState = 'out';
  }

  temporaryAddToMessage(): void {
    this.closeSendMessage();

    const attachments = [];
    this.filesToUpload.forEach(el => {
      attachments.push({
        displayName: el.name,
        downloadName: ''
      });
    });
    const message: RequestMessageList = {
      comment: this.f.comment.value,
      attachment: attachments[0],
      requestMessageId: '0',
      isUploading: true,
      sendBy: SendByType.CUSTOMER,
      sendDateUtc: new Date()
    };
    this.messages.push(message);
    this.filesToUpload = [];
    this.f.comment.patchValue('');
  }

  selectFile(fileInput: Event): void {
    const file = fileInput.target['files'][0];
    this.filesToUpload = [];
    if (this.utilsService.isFileValid(file, this.allowedExt, this.appConfig.fileUploadSize)) {
      this.filesToUpload.push(file);
      this.openSendMessage();
      if (this.isCryptoWithdraw) {
        if (this.uploadImageSubscription) {
          this.uploadImageSubscription.unsubscribe();
        }
        this.isLoadingUploadImage = true;
        this.uploadImageSubscription = this.requestMessageService.uploadFileMessageCryptoWithdraw(this.filesToUpload[0])
          .pipe(
            finalize(() => this.isLoadingUploadImage = false)
          )
          .subscribe(
            res => {
              this.form.patchValue({
                attachment: res.data.temporaryUrl,
                fileName: res.data.name
              })
            }
          )
      }
    } else {
      this.checkFileValidation(file, this.allowedExt, this.appConfig.fileUploadSize);
    }
  }
  removeFile(file: File): void {
    const index = this.filesToUpload.indexOf(file);
    this.filesToUpload.splice(index, 1);
    if (this.uploadImageSubscription) {
      this.uploadImageSubscription.unsubscribe();
    }
    if (this.isCryptoWithdraw) {
      this.f.attachment.setValue(null);
    }
  }

  checkFileValidation(file: File, allowedExt: string, maxSize: number): void {
    // check file extension
    if (
      !allowedExt.toUpperCase()
        .includes(
          file.name.toUpperCase().split('.').pop())
    ) {
      this.translateService.get('VALIDATION.FILE_EXTENSION', { value: allowedExt }).subscribe(res => {
        this.alertNewSnakbarService.snackBarError(res);
      });
    }

    // check file size
    if (file.size > maxSize) {
      let mbMaxSize = (maxSize / 1048578);
      if ((mbMaxSize % 1) != 0) {
        mbMaxSize = parseFloat(mbMaxSize.toFixed(2));
      }
      this.translateService.get('VALIDATION.FILE_MAX_SIZE', { value: mbMaxSize }).subscribe(res => {
        this.alertNewSnakbarService.snackBarError(res);
      });
    }
  }

  downloadDocument(message: RequestMessageList): void {
    if (this.isCryptoWithdraw) {
      // تشخیص نوع فایل از پسوند
      const fileExtension = message.attachment.displayName.split('.').pop()?.toLowerCase();

      // لیست فرمت‌هایی که باید دانلود شوند
      const downloadableExtensions = ['zip', 'rar', '7z', 'exe', 'iso'];

      // دانلود فایل
      const a = document.createElement('a');
      a.href = message.attachment.fileUrl;
      a.setAttribute('type', 'hidden');
      a.target = '_blank';
      if (downloadableExtensions.includes(fileExtension!)) {
        a.download = message.attachment.displayName;
      }
      a.click();
      a.remove();
    } else {
      this.downloadSubscription = this.requestMessageService
        .downloadDocument(this.requestId, message.attachment.downloadName)
        .subscribe((response) => {
          this.utilsService.downloadFile(response, message.attachment.displayName);
        });
    }
  }

}
